import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckCircle, Plus, Minus } from "lucide-react";
import PublicLayout from "@/components/PublicLayout";

const API = `${process.env.REACT_APP_BACKEND_URL || 'https://thebetterfare-backend-production.up.railway.app'}/api`;
const HOTEL_IMAGE = "/images/hotel.png";
const WHATSAPP_GROUP = "https://chat.whatsapp.com/FTpF0TyZG2nJkWpWKPdlrM?mode=hqrc";
const TURNSTILE_SITE_KEY = "0x4AAAAAAC8k_BC0s3zVYBE5";

const STAR_OPTIONS = [
  { value: "no_preference", label: "No preference" },
  { value: "3", label: "3 Star" },
  { value: "4", label: "4 Star" },
  { value: "5", label: "5 Star" },
  { value: "other", label: "Other" },
];

const ROOM_TYPE_OPTIONS = [
  { value: "no_preference", label: "No preference" },
  { value: "single", label: "Single" },
  { value: "double", label: "Double" },
  { value: "twin", label: "Twin" },
  { value: "triple", label: "Triple" },
  { value: "family", label: "Family" },
  { value: "other", label: "Other" },
];

const MEAL_PLAN_OPTIONS = [
  { value: "no_preference", label: "No preference" },
  { value: "room_only", label: "Room Only" },
  { value: "breakfast", label: "Breakfast Included" },
  { value: "half_board", label: "Half Board" },
  { value: "full_board", label: "Full Board" },
  { value: "all_inclusive", label: "All Inclusive" },
];

const today = new Date().toISOString().split("T")[0];

export default function HotelRequestForm() {
  useEffect(() => {
    document.title = "Hotel Booking Request | The Better Fare";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.content = "Request hotels with The Better Fare. Tell us your destination, dates, rooms and preferences and we'll help find suitable hotel options.";
  }, []);

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [requestNumber, setRequestNumber] = useState("");
  const [requestName, setRequestName] = useState("");
  const [consentGiven, setConsentGiven] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    destination: "",
    country: "",
    check_in: "",
    check_out: "",
    num_rooms: 1,
    adults: 2,
    children: 0,
    child_ages: [],
    has_specific_hotel: false,
    hotel_name_preference: "",
    star_rating: "no_preference",
    room_type: "no_preference",
    meal_plan: "no_preference",
    budget_per_night: "",
    special_requests: "",
  });

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const setChildAges = (idx, val) => {
    setForm(prev => {
      const ages = [...prev.child_ages];
      ages[idx] = val;
      return { ...prev, child_ages: ages };
    });
  };

  const setChildren = (n) => {
    const count = Math.max(0, Math.min(10, n));
    setForm(prev => {
      const ages = Array.from({ length: count }, (_, i) => prev.child_ages[i] || "");
      return { ...prev, children: count, child_ages: ages };
    });
  };

  const minCheckout = form.check_in || today;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim()) { toast.error("Please enter your full name"); return; }
    if (!form.email.trim()) { toast.error("Please enter your email address"); return; }
    if (!form.phone.trim()) { toast.error("Please enter your phone / WhatsApp number"); return; }
    if (!form.destination.trim()) { toast.error("Please enter your destination"); return; }
    if (!form.check_in) { toast.error("Please select a check-in date"); return; }
    if (!form.check_out) { toast.error("Please select a check-out date"); return; }
    if (form.check_out <= form.check_in) { toast.error("Check-out must be after check-in"); return; }
    if (form.num_rooms < 1 || form.num_rooms > 20) { toast.error("Please enter a valid number of rooms (1–20)"); return; }
    if (form.adults < 1 || form.adults > 50) { toast.error("Please enter a valid number of adults"); return; }
    if (!consentGiven) { toast.error("Please accept the terms and conditions"); return; }
    setLoading(true);
    try {
      const payload = {
        ...form,
        star_rating: form.star_rating === "no_preference" ? null : form.star_rating,
        room_type: form.room_type === "no_preference" ? null : form.room_type,
        meal_plan: form.meal_plan === "no_preference" ? null : form.meal_plan,
        hotel_name_preference: form.has_specific_hotel ? form.hotel_name_preference : null,
        child_ages: form.child_ages.filter(Boolean),
        consent_given: consentGiven,
        turnstile_token: turnstileToken,
      };
      const res = await axios.post(`${API}/hotel-requests`, payload);
      setRequestNumber(res.data.request_number);
      setRequestName(form.first_name);
      setSubmitted(true);
      toast.success("Hotel request submitted!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSubmitted(false);
    setRequestNumber("");
    setRequestName("");
    setTurnstileToken("");
    setConsentGiven(false);
    setForm({
      first_name: "", last_name: "", email: "", phone: "",
      destination: "", country: "", check_in: "", check_out: "",
      num_rooms: 1, adults: 2, children: 0, child_ages: [],
      has_specific_hotel: false, hotel_name_preference: "",
      star_rating: "no_preference", room_type: "no_preference",
      meal_plan: "no_preference", budget_per_night: "", special_requests: "",
    });
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="max-w-lg w-full text-center">
          <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-emerald-500" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4"
            style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 900 }}>
            Hotel Request Received
          </h2>
          <div className="bg-gray-50 rounded-xl p-6 mb-6 border border-gray-200">
            <p className="text-sm text-gray-500 mb-2">Your Request Reference</p>
            <p className="text-3xl font-mono font-bold text-gray-900 tracking-wider">{requestNumber}</p>
          </div>
          <p className="text-gray-600 mb-8">
            Thank you, {requestName}. We have received your hotel request and will review the requirements and contact you with suitable options. A confirmation has been sent to your email.
          </p>
          <Button onClick={resetForm} className="tbf-submit-btn rounded-md px-8 h-12">
            Submit Another Request
          </Button>
        </div>
      </div>
    );
  }

  return (
    <PublicLayout>
      <div>
        {/* Page Title */}
        <div className="text-center pt-10 pb-6">
          <h1 className="tbf-page-title">HOTEL REQUEST</h1>
        </div>

        {/* Hero */}
        <div className="max-w-6xl mx-auto px-6 pb-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="tbf-hero-heading mb-2">Tell us what you need and</h2>
              <h2 className="tbf-hero-heading-accent mb-6">we'll find the right hotel.</h2>
              <p className="text-gray-600 leading-relaxed mb-6" style={{ fontFamily: "'Poppins', sans-serif" }}>
                Tell us your destination, dates, and preferences and we'll find the best available options for you. This is a request — not an instant booking. We'll review your requirements and come back to you with suitable choices.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6" style={{ fontFamily: "'Poppins', sans-serif" }}>
                For fast communication, join our WhatsApp group using the same number you provide in the form.
              </p>
              <a href={WHATSAPP_GROUP} target="_blank" rel="noopener noreferrer" className="tbf-whatsapp-btn">
                The Better Fare | WhatsApp Group
              </a>
            </div>
            <div className="hidden lg:flex justify-center">
              <img src={HOTEL_IMAGE} alt="Hotel illustration" className="w-full max-w-md"
                onError={e => { e.target.style.display = "none"; }} />
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="tbf-form-section">
          <div className="max-w-5xl mx-auto px-6 py-12">
            <form onSubmit={handleSubmit} className="space-y-8">

              {/* Personal Details */}
              <div>
                <p className="tbf-label text-base font-bold text-gray-800 mb-4" style={{ fontSize: "0.95rem", fontWeight: 700 }}>Your Details</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  <div>
                    <label className="tbf-label">First Name <span className="text-red-500">*</span></label>
                    <input type="text" value={form.first_name} onChange={e => set("first_name", e.target.value)} required placeholder="E.g. John" className="tbf-input" />
                  </div>
                  <div>
                    <label className="tbf-label">Last Name <span className="text-red-500">*</span></label>
                    <input type="text" value={form.last_name} onChange={e => set("last_name", e.target.value)} required placeholder="E.g. Doe" className="tbf-input" />
                  </div>
                  <div>
                    <label className="tbf-label">Email <span className="text-red-500">*</span></label>
                    <input type="email" value={form.email} onChange={e => set("email", e.target.value)} required placeholder="E.g. john@doe.com" className="tbf-input" />
                  </div>
                  <div>
                    <label className="tbf-label">WhatsApp / Phone <span className="text-red-500">*</span></label>
                    <input type="text" value={form.phone} onChange={e => set("phone", e.target.value)} required placeholder="E.g. +31 6 1234567" className="tbf-input" />
                  </div>
                </div>
              </div>

              {/* Destination & Dates */}
              <div>
                <p className="tbf-label text-base font-bold text-gray-800 mb-4" style={{ fontSize: "0.95rem", fontWeight: 700 }}>Hotel Requirements</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                  <div>
                    <label className="tbf-label">Destination / City <span className="text-red-500">*</span></label>
                    <input type="text" value={form.destination} onChange={e => set("destination", e.target.value)} required placeholder="E.g. Dubai, Istanbul, Paris" className="tbf-input" />
                  </div>
                  <div>
                    <label className="tbf-label">Country</label>
                    <input type="text" value={form.country} onChange={e => set("country", e.target.value)} placeholder="E.g. UAE, Turkey, France" className="tbf-input" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                  <div>
                    <label className="tbf-label">Check-in Date <span className="text-red-500">*</span></label>
                    <input type="date" value={form.check_in} min={today}
                      onChange={e => {
                        set("check_in", e.target.value);
                        if (form.check_out && form.check_out <= e.target.value) set("check_out", "");
                      }}
                      required className="tbf-input" style={{ colorScheme: "light" }} />
                  </div>
                  <div>
                    <label className="tbf-label">Check-out Date <span className="text-red-500">*</span></label>
                    <input type="date" value={form.check_out} min={minCheckout}
                      onChange={e => set("check_out", e.target.value)}
                      required className="tbf-input" style={{ colorScheme: "light" }} />
                  </div>
                </div>

                {/* Rooms & Guests */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  <div>
                    <label className="tbf-label">Number of Rooms</label>
                    <div className="flex items-center gap-3 mt-1">
                      <button type="button" onClick={() => set("num_rooms", Math.max(1, form.num_rooms - 1))}
                        className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:border-purple-400 hover:text-purple-700">
                        <Minus size={14} />
                      </button>
                      <span className="w-8 text-center font-semibold text-gray-900">{form.num_rooms}</span>
                      <button type="button" onClick={() => set("num_rooms", Math.min(20, form.num_rooms + 1))}
                        className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:border-purple-400 hover:text-purple-700">
                        <Plus size={14} />
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="tbf-label">Adults</label>
                    <div className="flex items-center gap-3 mt-1">
                      <button type="button" onClick={() => set("adults", Math.max(1, form.adults - 1))}
                        className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:border-purple-400 hover:text-purple-700">
                        <Minus size={14} />
                      </button>
                      <span className="w-8 text-center font-semibold text-gray-900">{form.adults}</span>
                      <button type="button" onClick={() => set("adults", Math.min(50, form.adults + 1))}
                        className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:border-purple-400 hover:text-purple-700">
                        <Plus size={14} />
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="tbf-label">Children (under 12)</label>
                    <div className="flex items-center gap-3 mt-1">
                      <button type="button" onClick={() => setChildren(form.children - 1)}
                        className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:border-purple-400 hover:text-purple-700">
                        <Minus size={14} />
                      </button>
                      <span className="w-8 text-center font-semibold text-gray-900">{form.children}</span>
                      <button type="button" onClick={() => setChildren(form.children + 1)}
                        className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:border-purple-400 hover:text-purple-700">
                        <Plus size={14} />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Child ages */}
                {form.children > 0 && (
                  <div className="mt-4">
                    <label className="tbf-label">Children's Ages</label>
                    <div className="flex flex-wrap gap-3 mt-1">
                      {Array.from({ length: form.children }, (_, i) => (
                        <div key={i} className="flex flex-col items-center gap-1">
                          <span className="text-xs text-gray-500">Child {i + 1}</span>
                          <input
                            type="number" min="0" max="11"
                            value={form.child_ages[i] || ""}
                            onChange={e => setChildAges(i, e.target.value)}
                            placeholder="Age"
                            className="tbf-input w-20 text-center"
                            style={{ padding: "6px 8px" }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Hotel Preferences */}
              <div>
                <p className="tbf-label text-base font-bold text-gray-800 mb-4" style={{ fontSize: "0.95rem", fontWeight: 700 }}>Hotel Preferences</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-5">
                  <div>
                    <label className="tbf-label">Hotel Category</label>
                    <Select value={form.star_rating} onValueChange={v => set("star_rating", v)}>
                      <SelectTrigger className="tbf-select-trigger"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STAR_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="tbf-label">Room Type</label>
                    <Select value={form.room_type} onValueChange={v => set("room_type", v)}>
                      <SelectTrigger className="tbf-select-trigger"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ROOM_TYPE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="tbf-label">Meal Plan</label>
                    <Select value={form.meal_plan} onValueChange={v => set("meal_plan", v)}>
                      <SelectTrigger className="tbf-select-trigger"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {MEAL_PLAN_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="tbf-label">Approximate Budget per Room / Night (optional)</label>
                  <input type="text" value={form.budget_per_night} onChange={e => set("budget_per_night", e.target.value)} placeholder="E.g. €100–€150 per night" className="tbf-input" style={{ maxWidth: 320 }} />
                </div>
              </div>

              {/* Specific hotel */}
              <div style={{ border: "1px solid #e9d5ff", borderRadius: 12, padding: "20px 24px", background: "#faf5ff" }}>
                <div className="flex items-center gap-3 mb-3">
                  <Checkbox
                    id="specific-hotel"
                    checked={form.has_specific_hotel}
                    onCheckedChange={v => set("has_specific_hotel", !!v)}
                    className="border-gray-400 data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-600"
                  />
                  <label htmlFor="specific-hotel" className="text-sm font-semibold text-gray-800 cursor-pointer">
                    I have a specific hotel in mind
                  </label>
                </div>
                {form.has_specific_hotel && (
                  <div className="mt-3">
                    <label className="tbf-label">Hotel Name</label>
                    <input
                      type="text"
                      value={form.hotel_name_preference}
                      onChange={e => set("hotel_name_preference", e.target.value)}
                      placeholder="E.g. Atlantis The Palm, Dubai"
                      className="tbf-input"
                      style={{ background: "#fff" }}
                    />
                    <p className="text-xs text-gray-400 mt-1">Providing a hotel name is a preference — we cannot guarantee availability until confirmed.</p>
                  </div>
                )}
              </div>

              {/* Special requests */}
              <div>
                <label className="tbf-label">Special Requests or Requirements</label>
                <textarea
                  value={form.special_requests}
                  onChange={e => set("special_requests", e.target.value)}
                  placeholder="E.g. connecting rooms, high floor, accessible room, sea view, cot for infant, late check-in, location preferences..."
                  rows={4}
                  className="tbf-input resize-none"
                />
              </div>

              {/* Turnstile */}
              <div>
                <div
                  className="cf-turnstile"
                  data-sitekey={TURNSTILE_SITE_KEY}
                  ref={(el) => {
                    if (el && window.turnstile && !el.dataset.rendered) {
                      el.dataset.rendered = "true";
                      window.turnstile.render(el, {
                        sitekey: TURNSTILE_SITE_KEY,
                        callback: (token) => setTurnstileToken(token),
                      });
                    }
                  }}
                />
              </div>

              {/* Consent */}
              <div className="flex items-start gap-3">
                <Checkbox
                  id="consent"
                  checked={consentGiven}
                  onCheckedChange={setConsentGiven}
                  className="mt-0.5 border-gray-400 data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-600"
                />
                <label htmlFor="consent" className="text-sm text-gray-600 leading-relaxed cursor-pointer">
                  By submitting this form, you agree to our{" "}
                  <Link to="/terms" className="text-purple-600 hover:underline font-medium">Terms and Conditions</Link>
                  {" "}and{" "}
                  <Link to="/privacy" className="text-purple-600 hover:underline font-medium">Privacy Policy</Link>.
                  We will use your details to respond to this hotel request only.
                </label>
              </div>

              <Button
                type="submit"
                className="tbf-submit-btn w-full h-14 text-base font-semibold"
                disabled={loading || !consentGiven || !turnstileToken}
              >
                {loading ? "Submitting..." : "Send Hotel Request"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
